import React, { useState, useEffect } from 'react';
import { apiFetch, getApiHost } from '../utils/api';

interface TibaneStakingData {
  mint: string;
  poolAddress: string;
  vaultAddress: string;
  totalStaked: string;
  totalStakedPercent: number;
  tauSeconds: number;
  stakerCount: number;
  decimals: number;
}

interface Props {
  mint: string;
}

function formatTokenAmount(raw: string, decimals: number): string {
  const num = Number(raw) / Math.pow(10, decimals);
  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
  return num.toFixed(2);
}

function formatTau(seconds: number): string {
  const days = seconds / 86400;
  if (days >= 1) return `${days}d`;
  const hours = seconds / 3600;
  return `${hours.toFixed(0)}h`;
}

const TibaneStaking: React.FC<Props> = ({ mint }) => {
  const [data, setData] = useState<TibaneStakingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchStaking = async () => {
      try {
        setLoading(true);
        const res = await apiFetch(`${getApiHost()}/api/tibane-staking/${mint}`);
        if (!res.ok) throw new Error();
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStaking();
    return () => { cancelled = true; };
  }, [mint]);

  // Don't render if loading, error, or no stakers
  if (loading || error || !data || data.stakerCount === 0 || data.totalStakedPercent === 0) return null;

  const { totalStakedPercent, stakerCount, decimals, tauSeconds, poolAddress } = data;

  return (
    <div className="mb-6">
      <h3 className="text-xl font-bold mb-4 flex items-center">
        <i className="fas fa-coins text-purple-400 mr-3"></i>
        Staked Supply
        <a
          href={`https://www.tibane.net/staking/${mint}`}
          target="_blank"
          rel="noopener noreferrer"
          className="ml-2 text-sm font-normal text-purple-400 hover:text-purple-300 transition-colors"
        >
          via Tibane Labs <i className="fas fa-external-link-alt text-xs ml-1"></i>
        </a>
      </h3>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Tokens Staked</div>
          <div className="text-white font-bold text-lg">
            {formatTokenAmount(data.totalStaked, decimals)}
          </div>
        </div>
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">% of Total Supply</div>
          <div className="text-purple-400 font-bold text-lg">
            {totalStakedPercent.toFixed(2)}%
          </div>
        </div>
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Active Stakers</div>
          <div className="text-white font-bold text-lg">
            {stakerCount}
          </div>
        </div>
        <div className="bg-crypto-card border border-gray-700 rounded-lg p-4">
          <div className="text-sm text-gray-400 mb-1">Time Constant (τ)</div>
          <div className="text-purple-400 font-bold text-lg">
            {formatTau(tauSeconds)}
          </div>
        </div>
      </div>

      <div className="text-center text-xs text-gray-600">
        Staking data sourced from{' '}
        <a
          href={`https://solscan.io/account/${poolAddress}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-purple-400/60 hover:text-purple-300 transition-colors"
        >
          on-chain pool
        </a>
        {' '}via Tibane Labs ChiefStaker program
      </div>
    </div>
  );
};

export default TibaneStaking;
